/**
 * Live status of in-flight bulk sends, keyed by batch id.
 *
 * Module-level rather than component state, for one specific reason: the dock
 * panel unmounts entirely when the dock is closed (`ChatDock` renders only the
 * bubble), so anything the panel owns dies with it. A send that keeps running
 * server-side while its progress view has been thrown away is exactly the
 * failure this feature exists to fix. Same pattern as `utils/syncQueueStore.js`
 * and the resync listener sets in `RoomsUnreadProvider`.
 *
 * Fed from `CommsNotifyProvider` — which is mounted app-wide — not from the
 * dock, so progress keeps arriving whether or not anyone is looking at it.
 *
 * **Memory only.** `utils/listCache/caches.js` marks every PII cache
 * memory-only by explicit policy, and a batch holds a message body and the
 * clients it went to. Nothing here goes to localStorage; a reload loses the
 * view of a send, never the send.
 */

/** Per-room status. `unknown` = the events stopped arriving, not a failure. */
export const ROOM_SENDING = "sending";
export const ROOM_SENT = "sent";
export const ROOM_FAILED = "failed";
export const ROOM_UNKNOWN = "unknown";

/** @type {Map<string, {total: number, byRoom: Map<string, string>, errors: Map<string, string>, finishedAt: number|null, lastEventAt: number}>} */
const batches = new Map();
let snapshot = {};
const listeners = new Set();

/**
 * Batches are small and short-lived, but nothing prunes them on its own — a
 * long-lived tab doing many sends would accumulate. Keep the most recent few.
 */
const MAX_TRACKED_BATCHES = 10;

const publish = () => {
  snapshot = Object.fromEntries(
    Array.from(batches.entries()).map(([id, b]) => [id, toView(b)])
  );
  listeners.forEach((fn) => fn());
};

/** A plain, render-ready projection — components never touch the Maps. */
const toView = (batch) => {
  const statuses = Array.from(batch.byRoom.values());
  return {
    total: batch.total,
    byRoom: Object.fromEntries(batch.byRoom),
    errors: Object.fromEntries(batch.errors),
    sent: statuses.filter((s) => s === ROOM_SENT).length,
    failed: statuses.filter((s) => s === ROOM_FAILED).length,
    pending: statuses.filter((s) => s === ROOM_SENDING).length,
    unknown: statuses.filter((s) => s === ROOM_UNKNOWN).length,
    finishedAt: batch.finishedAt,
    lastEventAt: batch.lastEventAt,
  };
};

export const subscribeBulkSends = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getBulkSendSnapshot = () => snapshot;

/** One batch, or undefined. Components read through this. */
export const getBulkSend = (batchId) => (batchId ? snapshot[batchId] : undefined);

/**
 * A RESUME HINT, READ ONCE ON MOUNT — not a live "what should I be showing".
 *
 * The most recently started batch still being tracked, or null. It exists so a
 * send survives its pane unmounting: close the dock mid-send, reopen it, and
 * this is how the pane finds the batch again instead of offering an empty
 * composer. That is the whole point of fire-and-forget.
 *
 * WHAT IT IS NOT: an answer to "is a send in progress". A FINISHED batch is
 * still returned — `clearBulkSend` is the only thing that removes one, so
 * dismissing is always an explicit act. `BulkComposePane` therefore reads this
 * once, to seed the batch it owns, and never re-reads it per render. It used to,
 * and a finished batch nobody had dismissed then held the composer hostage:
 * every compose after a successful send reopened the old progress view.
 *
 * Insertion order is Map order, and a coach realistically has one batch in
 * flight at a time; the newest entry is the right answer for the case that
 * matters and a harmless one otherwise.
 */
export const getActiveBatchId = () => {
  const ids = Object.keys(snapshot);
  return ids.length ? ids[ids.length - 1] : null;
};

const trim = () => {
  while (batches.size > MAX_TRACKED_BATCHES) {
    const oldest = batches.keys().next().value;
    batches.delete(oldest);
  }
};

/**
 * Seed a batch the moment the POST is accepted, so every recipient shows as
 * sending immediately rather than after the first event arrives.
 *
 * @param {string} batchId
 * @param {string[]} roomIds
 */
export const startBulkSend = (batchId, roomIds) => {
  if (!batchId) return;
  batches.set(batchId, {
    total: roomIds.length,
    byRoom: new Map(roomIds.map((id) => [String(id), ROOM_SENDING])),
    errors: new Map(),
    finishedAt: null,
    lastEventAt: Date.now(),
  });
  trim();
  publish();
};

/**
 * Apply one `bulk_send_*` event from the coach's personal channel.
 *
 * Tolerates a missing `started` — the POST already seeded the batch, and on the
 * rare ordering where an event beats the HTTP response we create the batch from
 * the event rather than dropping it.
 *
 * @param {{type: string, batch_id: string, room_id?: string, ok?: boolean,
 *   error?: string|null, total?: number, room_ids?: string[]}} event
 */
export const applyBulkSendEvent = (event) => {
  const batchId = event?.batch_id;
  if (!batchId || typeof event.type !== "string") return;

  let batch = batches.get(batchId);
  if (!batch) {
    if (event.type === "bulk_send_done") return; // nothing left to show
    batch = {
      total: event.total || (event.room_ids || []).length,
      byRoom: new Map((event.room_ids || []).map((id) => [String(id), ROOM_SENDING])),
      errors: new Map(),
      finishedAt: null,
      lastEventAt: Date.now(),
    };
    batches.set(batchId, batch);
    trim();
  }
  batch.lastEventAt = Date.now();

  if (event.type === "bulk_send_progress") {
    const roomId = String(event.room_id);
    const next = event.ok ? ROOM_SENT : ROOM_FAILED;
    // Publish only on a real change — a burst of a hundred events would
    // otherwise re-render every subscriber a hundred times for nothing. Same
    // guard syncQueueStore uses, and for the same reason.
    if (batch.byRoom.get(roomId) === next) return;
    batch.byRoom.set(roomId, next);
    if (event.error) batch.errors.set(roomId, event.error);
    else batch.errors.delete(roomId);
  } else if (event.type === "bulk_send_done") {
    if (batch.finishedAt) return;
    batch.finishedAt = Date.now();
    // Anything still `sending` when the server says it's finished never got its
    // progress event. It is not a failure — the send happened.
    for (const [roomId, status] of batch.byRoom) {
      if (status === ROOM_SENDING) batch.byRoom.set(roomId, ROOM_UNKNOWN);
    }
  } else if (event.type !== "bulk_send_started") {
    return;
  }

  publish();
};

/**
 * Give up waiting on a batch: everything still sending becomes `unknown`.
 *
 * Called when the events dry up — the socket dropped, or reconnected and the
 * publications in the gap are gone (the `broadcast` namespace has no Centrifugo
 * history, so they are not replayed). Deliberately NOT `failed`: the messages
 * almost certainly landed, and inviting a retry for them would be worse than
 * saying honestly that we stopped hearing about it.
 */
export const settleBulkSend = (batchId) => {
  const batch = batches.get(batchId);
  if (!batch || batch.finishedAt) return;
  let changed = false;
  for (const [roomId, status] of batch.byRoom) {
    if (status === ROOM_SENDING) {
      batch.byRoom.set(roomId, ROOM_UNKNOWN);
      changed = true;
    }
  }
  if (!changed) return;
  batch.finishedAt = Date.now();
  publish();
};

/** Drop a batch once the operator has moved on. */
export const clearBulkSend = (batchId) => {
  if (batches.delete(batchId)) publish();
};
